<template>
  <div class="new-app-bundle-flow">
    <AnimatedProcessIndicator
      v-if="isProcessing"
      :text="$t('APP.RELEASES.BUNDLE.IS_PROCESSING')"
    />

    <BundleSignUploader
      v-if="isWaitingForSign"
      :upload-path="binFileUploadPath"
      :aab-link="bundle.bundleLink"
      @upload-done="handleSignUpload"
    />

    <template v-if="errors.length">
      <ElAlert
        v-for="error in errors"
        :key="error"
        :title="$t('ERROR', { error })"
        :closable="false"
        type="error"
        show-icon
        class="new-app-bundle-flow__error"
      >
        {{ error }}
      </ElAlert>

      <ElButton
        type="danger"
        icon="el-icon-delete"
        size="small"
        :loading="isDeletingRelease"
        @click="deleteDraftAppRelease"
      >
        {{ $t('APPS.DRAFT.STEPS.UPLOAD_FILES.DELETE_RELEASE') }}
      </ElButton>
    </template>
  </div>
</template>

<script>
import AnimatedProcessIndicator from '@@/common/AnimatedProcessIndicator';
import BundleSignUploader from '@@/app/common/BundleSignUploader';

import { BUNDLE_STATES } from '@@/app/common/constants';
import { apiPath } from '@/api/constants';
import { DRAFT_APP, UPDATE_DRAFT_APP } from '@/store/types';
import { mapActions } from 'vuex';
import { DraftApps } from '@/api/api';

const BUNDLE_FETCH_INTERVAL_TIME = 5 * 1000;

export default {
  name: 'NewAppBundleFlow',
  components: {
    AnimatedProcessIndicator,
    BundleSignUploader,
  },

  props: {
    bundle: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      bundleFetchInterval: null,
      isDeletingRelease: false,
    };
  },

  computed: {
    bundleState() {
      return this.bundle?.state.toUpperCase();
    },
    isProcessing() {
      return (
        this.bundleState === BUNDLE_STATES.PROCESSING ||
        this.bundleState === BUNDLE_STATES.SIGNED
      );
    },
    isWaitingForSign() {
      return this.bundleState === BUNDLE_STATES.UPLOADED;
    },
    errors() {
      return this.bundle?.errors || [];
    },
    binFileUploadPath() {
      const appId = this.$route.params.appId;
      return `${apiPath}/draft-apps/${appId}/upload/bin/`;
    },
  },
  watch: {
    isProcessing: {
      immediate: true,
      handler() {
        if (this.isProcessing && !this.bundleFetchInterval) {
          this.bundleFetchInterval = setInterval(() => {
            this.fetchBundle();
          }, BUNDLE_FETCH_INTERVAL_TIME);
        }
        if (!this.isProcessing) {
          clearInterval(this.bundleFetchInterval);
        }
      },
    },
  },
  beforeDestroy() {
    clearInterval(this.bundleFetchInterval);
  },
  methods: {
    ...mapActions({
      refetchDraftApp: DRAFT_APP,
      updateDraftAppData: UPDATE_DRAFT_APP,
    }),
    fetchBundle() {
      const appId = this.$route.params.appId;

      this.refetchDraftApp(appId)
        .then(() => {
          if (this.bundle.state === BUNDLE_STATES.DONE) {
            clearInterval(this.bundleFetchInterval);
          }
        })
        .catch(({ message }) => {
          this.$message.error(message);
        });
    },
    handleSignUpload({ message, draftApp }) {
      if (message) this.$message.success(message);
      if (draftApp) this.updateDraftAppData(draftApp);
    },
    deleteDraftAppRelease() {
      const appId = this.$route.params.appId;
      this.isDeletingRelease = true;

      return DraftApps.deleteRelease(appId)
        .then(({ message, draftApp }) => {
          if (message) this.$message.success(message);
          if (draftApp) this.updateDraftAppData(draftApp);
        })
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => (this.isDeletingRelease = false));
    },
  },
};
</script>

<style lang="scss">
.new-app-bundle-flow {
  &__error {
    margin-bottom: $global-margin;
  }
}
</style>
